import { memo } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { Clinic } from '../../../models/MedVox/Clinic';
import {
  CLINIC_SELECT_ALL_LABEL,
  CLINIC_SELECT_ALL_VALUE,
  SelectAllBehavior,
  useClinicSelectAllOptions,
} from '../../../hooks/shared/useClinicSelectAllOptions';
import { OptionLike } from '../../../hooks/shared/useFilterOption';

type ClinicFilterOption =
  | boolean
  | ((inputValue: string, option?: OptionLike) => boolean);

type ClinicSelectProps = {
  clinics: Clinic[];
  value?: string[];
  onChange: (value: string[]) => void;
  selectAllBehavior?: SelectAllBehavior;
  className?: string;
  placeholder?: string;
  filterOption?: ClinicFilterOption;
  onOpenChange?: SelectProps['onOpenChange'];
  optionRender?: SelectProps['optionRender'];
  disabled?: boolean;
  showSearch?: boolean;
  showOnlyAllOption?: boolean;
};

const ALL_OPTION = [
  { label: CLINIC_SELECT_ALL_LABEL, value: CLINIC_SELECT_ALL_VALUE },
];

export const ClinicSelect = memo(
  ({
    clinics,
    value = [],
    onChange,
    selectAllBehavior = 'clear-filter',
    className,
    placeholder = 'Все',
    filterOption,
    onOpenChange,
    optionRender,
    disabled,
    showSearch = true,
    showOnlyAllOption = false,
  }: ClinicSelectProps) => {
    const { options, displaySelectedClinicIds, normalizeSelectedClinicIds } =
      useClinicSelectAllOptions(clinics, value, selectAllBehavior);

    const selectOptions = showOnlyAllOption ? ALL_OPTION : options;
    const selectValue = showOnlyAllOption
      ? [CLINIC_SELECT_ALL_VALUE]
      : displaySelectedClinicIds;
    const selectFilterOption: SelectProps['filterOption'] =
      typeof filterOption === 'function'
        ? (inputValue, option) =>
            filterOption(inputValue, {
              label: option?.label,
              value: option?.value,
            })
        : filterOption;

    return (
      <Select
        allowClear
        className={className}
        placeholder={placeholder}
        mode="multiple"
        value={selectValue}
        filterOption={selectFilterOption}
        onChange={(nextValue) =>
          onChange(normalizeSelectedClinicIds(nextValue))
        }
        showSearch={showSearch}
        onOpenChange={onOpenChange}
        removeIcon={null}
        maxTagCount="responsive"
        maxTagTextLength={24}
        options={selectOptions}
        autoClearSearchValue={false}
        optionRender={optionRender}
        disabled={disabled}
      />
    );
  },
);

ClinicSelect.displayName = 'ClinicSelect';

export type { ClinicSelectProps };
